import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import { useMediaQuery, useTheme } from '@mui/material';
import { setNotifier } from '../services/ServiceUtils';
import { NOTIFICATION_AUTO_HIDE_DURATION_MS } from '../config';

const NotificationContext = createContext(null);

const normalizeLevel = (level) => {
  const normalized = String(level || 'info').toLowerCase();
  if (['success', 'error', 'warning', 'info'].includes(normalized)) {
    return normalized;
  }
  return 'info';
};

const formatDetails = (details) => {
  if (!details) return '';
  if (Array.isArray(details)) {
    return details.filter(Boolean).join(', ');
  }
  if (typeof details === 'string') {
    return details;
  }
  return '';
};

export const NotificationProvider = ({ children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [snackPack, setSnackPack] = useState([]);
  const [open, setOpen] = useState(false);
  const [messageInfo, setMessageInfo] = useState(undefined);

  const notify = useCallback(({ level, message, details, duration }) => {
    setSnackPack((prev) => [
      ...prev,
      {
        key: new Date().getTime(),
        level: normalizeLevel(level),
        message,
        details,
        duration
      }
    ]);
  }, []);

  useEffect(() => {
    if (snackPack.length && !messageInfo) {
      setMessageInfo({ ...snackPack[0] });
      setSnackPack((prev) => prev.slice(1));
      setOpen(true);
    } else if (snackPack.length && messageInfo && open) {
      setOpen(false);
    }
  }, [snackPack, messageInfo, open]);

  const handleClose = useCallback((event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpen(false);
  }, []);

  const handleExited = useCallback(() => {
    setMessageInfo(undefined);
  }, []);

  useEffect(() => {
    setNotifier(notify);
    return () => setNotifier(null);
  }, [notify]);

  const value = useMemo(() => ({ notify }), [notify]);

  const detailsText = formatDetails(messageInfo?.details);

  return (
    <NotificationContext.Provider value={value}>
      {children}
      <Snackbar
        key={messageInfo ? messageInfo.key : undefined}
        open={open}
        autoHideDuration={messageInfo?.duration ?? NOTIFICATION_AUTO_HIDE_DURATION_MS}
        onClose={handleClose}
        slotProps={{ transition: { onExited: handleExited } }}
        anchorOrigin={isMobile ? { vertical: 'bottom', horizontal: 'center' } : { vertical: 'bottom', horizontal: 'right' }}
        sx={isMobile ? { width: '100%', px: 2, pb: 1 } : undefined}
      >
        <Alert
          onClose={handleClose}
          severity={messageInfo?.level || 'info'}
          variant="filled"
          sx={isMobile ? { width: '100%' } : undefined}
        >
          <div>{messageInfo?.message}</div>
          {detailsText ? <div>{detailsText}</div> : null}
        </Alert>
      </Snackbar>
    </NotificationContext.Provider>
  );
};

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};
